import React, { useCallback, useRef } from 'react';
import { SkeletonText } from '../../../../../../../../../../../shared/components/Skeleton';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { withWindow } from '@shared/utils/withWindow';
import { ButtonV2, Flex, StyleSystemProps, useDisclosure } from '@withjoy/joykit';
import { ReactComponent as CheckIcon } from '@assets/icons/mark-check-icon.svg';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ReactComponent as PlayArrow } from '@assets/icons/play-arrow.svg';
import { ChevronDown } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet, useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useCatalogRouting } from '@apps/registry/common/components/Catalog/useCatalogRouting';
import { useVimeoPlayerContext } from '@apps/registry/common/components/Catalog/utils/vimeoPlayer';
import { CustomCta } from '../../../../../CurationsIndex/components/CurationHeroHeader/components/CustomCta/CustomCta';
import { CurationVideoPlayerDialog } from '@apps/registry/common/components/Catalog/components/CurationVideoPlayer/CurationVideoPlayerDialog';
import { ProductHeroHorizontalAlignment, ProductHeroRedirectUrlType, ProductHeroTextColorMode, ProductHeroContentFieldsFragment } from '@graphql/generated';
import {
  styles,
  StyledWrapper,
  StyledMediaContainer,
  StyledGradient,
  StyledBackgroundVideo,
  StyledBackgroundVideoContainer,
  StyledBackgroundImage,
  StyledContainer,
  StyledContent,
  StyledEyebrow,
  StyledHed,
  StyledSubtitle,
  StyledBlockText,
  StyledButtonsContainer,
  getTextColor,
  StyledCheckListWrapper,
  StyledCheckList,
} from './Hero.styles';
import { useAuth } from '@shared/components/AuthProvider';

const ABSOLUTE_PATH_REGEX = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

export enum ProductHeroVerticalAlignment {
  center = 'center',
  bottom = 'bottom',
}
export interface HeroDataProps extends Omit<ProductHeroContentFieldsFragment, 'eyebrow' | 'description'> {
  eyebrow?: string;
  description?: string;
  checklist?: string[];
  ctaLink?: {
    text: string;
    publicUrlPath: string;
    redirectUrlType: ProductHeroRedirectUrlType;
  };
  verticalAlignment?: ProductHeroVerticalAlignment;
  shortenTextBlock?: boolean;
  showDescriptionOnMobile?: boolean;
  disableCtaOnAuth?: boolean;
  isOverlayGradient?: boolean;
  showChevronDown?: boolean;
}

interface HeroProps {
  data: HeroDataProps;
  refQueryParam?: 'sr_curations_hero_section';
  onViewCollectionClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => void;
  onOpenVideoPlayer?: (id: string) => void;
  customWrapperStyles?: StyleSystemProps;
}

const Hero: React.FC<HeroProps> = props => {
  const { data, refQueryParam, onViewCollectionClick, onOpenVideoPlayer, customWrapperStyles } = props;
  const {
    id,
    eyebrow,
    title,
    description,
    watchVideoUrl,
    horizontalAlignment,
    verticalAlignment,
    gradientMode,
    showDescriptionOnMobile,
    gradientCustomColorValue,
    textColorMode,
    textCustomColorValue,
    shortenTextBlock,
    cta,
    desktopMedia,
    tabletMedia,
    mobileMedia,
    checklist,
    ctaLink,
    isOverlayGradient,
    disableCtaOnAuth,
    showChevronDown,
  } = data;
  const { t } = useTranslation('catalogRegistry');
  const { billboard } = t('curationHeroHeader');
  const isMobile = useIsMobileScreen();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { goToCategory } = useCatalogRouting();
  const { isLoggedIn } = useAuth();
  const shouldHideCTA = isLoggedIn && disableCtaOnAuth;
  const hasChecklist = !!checklist;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { VimeoPlayer } = useVimeoPlayerContext();

  const horizontalAlign = isMobile ? ProductHeroHorizontalAlignment.center : horizontalAlignment;
  const verticalAlign = verticalAlignment ?? (hasChecklist ? ProductHeroVerticalAlignment.center : ProductHeroVerticalAlignment.bottom);
  const mediaAssets = isMobile ? mobileMedia : isMobileOrTablet && tabletMedia ? tabletMedia : desktopMedia;
  const isVideoAsset = mediaAssets.__typename === 'ProductHeroVideo';
  const isPhotoAsset = mediaAssets.__typename === 'ProductHeroImage';

  const hasAbsolutePath = useEventCallback((url: string): boolean => ABSOLUTE_PATH_REGEX.test(url));

  const handleOnViewCollectionClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    e => {
      onViewCollectionClick?.(e, id);
      if (ProductHeroRedirectUrlType.inApp === cta.redirectUrlType) {
        if (hasAbsolutePath(cta.publicUrlPath)) {
          withWindow(window => (window.location.href = cta.publicUrlPath));
        } else {
          goToCategory(`${cta.publicUrlPath}`, 'push', refQueryParam ? `?ref=${refQueryParam}` : undefined);
        }
      } else {
        withWindow(window => window.open(cta.publicUrlPath, '_blank'));
      }
    },
    [id, cta, refQueryParam, goToCategory, onViewCollectionClick, hasAbsolutePath],
  );

  const handleOnLinkClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    e => {
      onViewCollectionClick?.(e, id);
      if (ProductHeroRedirectUrlType.inApp === ctaLink?.redirectUrlType) {
        if (hasAbsolutePath(ctaLink?.publicUrlPath)) {
          withWindow(window => (window.location.href = ctaLink?.publicUrlPath));
        } else {
          goToCategory(`${ctaLink?.publicUrlPath}`, 'push', refQueryParam ? `?ref=${refQueryParam}` : undefined);
        }
      } else {
        withWindow(window => window.open(ctaLink?.publicUrlPath, '_blank'));
      }
    },
    [id, ctaLink, refQueryParam, goToCategory, onViewCollectionClick, hasAbsolutePath],
  );

  const handleOnWatchVideoClick = useEventCallback<React.MouseEventHandler<HTMLButtonElement>>(e => {
    onOpen();
    onOpenVideoPlayer?.(id);
  });

  const handleOnCloseVideoDialog = useEventCallback(() => {
    onClose();
    VimeoPlayer && iframeRef.current && new VimeoPlayer(iframeRef.current).play();
  });

  return (
    <StyledWrapper
      __css={customWrapperStyles ?? styles.wrapper}
      key={`${isMobile}${isMobileOrTablet}`}
    >
      <StyledContainer __css={styles.container(horizontalAlign, verticalAlign, hasChecklist)}>
        <StyledContent __css={styles.content(textColorMode, horizontalAlign, textCustomColorValue)}>
          <StyledBlockText __css={styles.blockText(horizontalAlign, shortenTextBlock ?? !hasChecklist)}>
            {eyebrow && <StyledEyebrow __css={styles.eyebrow}>{eyebrow}</StyledEyebrow>}
            {title && (
              <StyledHed
                as="h1"
                __css={styles.hed}
                maxWidth={hasChecklist ? [pxToRem(340), null, null, pxToRem(440)] : '100%'}
              >
                {title}
              </StyledHed>
            )}
            {description && (isMobile ? showDescriptionOnMobile : true) && <StyledSubtitle {...styles.subtitle}>{description}</StyledSubtitle>}
            {checklist && (
              <StyledCheckListWrapper __css={styles.checkListWrapper(horizontalAlign)}>
                {checklist.map((checklistString, idx) => (
                  <Flex
                    key={`checklist-${idx}`}
                    alignItems={['flex-start', 'center']}
                    columnGap={3}
                  >
                    <CheckIcon
                      height={pxToRem(24)}
                      width={pxToRem(24)}
                    />
                    <StyledCheckList
                      typographyVariant="body2"
                      __css={styles.checkList}
                    >
                      {checklistString}
                    </StyledCheckList>
                  </Flex>
                ))}
              </StyledCheckListWrapper>
            )}
            {showChevronDown && (
              <ChevronDown
                color="white"
                size={24}
                marginTop={3}
              />
            )}
          </StyledBlockText>
          {!shouldHideCTA && (
            <StyledButtonsContainer __css={styles.buttonsContainer(textColorMode, textCustomColorValue, !!ctaLink)}>
              <ButtonV2
                data-method="ctaButton"
                shape="rounded"
                variant="outline"
                typographyVariant="button2"
                borderColor="white"
                onClick={handleOnViewCollectionClick}
              >
                {cta.text}
              </ButtonV2>
              {ctaLink && (
                <CustomCta
                  method="curationsHeroSection"
                  variant="link"
                  typographyVariant="button2"
                  lineHeight={pxToRem(48)}
                  isPlainText
                  onClick={handleOnLinkClick}
                  buttonText={ctaLink.text}
                />
              )}
              {watchVideoUrl && (
                <ButtonV2
                  variant="outline"
                  shape="rounded"
                  typographyVariant="button2"
                  borderColor="white"
                  startIcon={
                    <PlayArrow
                      height="14px"
                      width="13px"
                    />
                  }
                  onClick={handleOnWatchVideoClick}
                >
                  {billboard.watchButton()}
                </ButtonV2>
              )}
              {watchVideoUrl && (
                <CurationVideoPlayerDialog
                  curationId={id}
                  videoUrl={watchVideoUrl}
                  eyebrow={eyebrow}
                  title={title}
                  onExploreCurationClick={handleOnViewCollectionClick}
                  isOpen={isOpen}
                  onClose={handleOnCloseVideoDialog}
                />
              )}
            </StyledButtonsContainer>
          )}
        </StyledContent>
      </StyledContainer>
      <StyledMediaContainer __css={styles.mediaContainer}>
        <StyledGradient __css={styles.gradient(isMobile, horizontalAlign, gradientMode, gradientCustomColorValue, isOverlayGradient)} />
        {isVideoAsset && (
          <StyledBackgroundVideo __css={styles.backgroundVideo(mediaAssets.video.fallbackImage.url)}>
            <StyledBackgroundVideoContainer __css={styles.backgroundVideoContainer}>
              <iframe
                ref={iframeRef}
                src={`${mediaAssets.video.videoUrl}&background=1`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={`${eyebrow}-${title}`}
              />
            </StyledBackgroundVideoContainer>
          </StyledBackgroundVideo>
        )}
        {isPhotoAsset && <StyledBackgroundImage __css={styles.backgroundImage(mediaAssets.photo.image.url)} />}
      </StyledMediaContainer>
    </StyledWrapper>
  );
};

const HeroSkeleton = () => {
  const isMobile = useIsMobileScreen();
  const horizontalAlign = isMobile ? ProductHeroHorizontalAlignment.center : ProductHeroHorizontalAlignment.left;
  return (
    <StyledWrapper
      __css={styles.wrapper}
      backgroundColor="mono2"
    >
      <StyledContainer __css={styles.container(horizontalAlign, ProductHeroVerticalAlignment.center)}>
        <StyledContent __css={styles.content(ProductHeroTextColorMode.light, horizontalAlign)}>
          <StyledBlockText __css={styles.blockText(horizontalAlign, true)}>
            <SkeletonText
              rows={1}
              skeletonHeight={[pxToRem(30), null, pxToRem(35)]}
              width={[pxToRem(130), null, pxToRem(190)]}
              marginBottom={3}
            />
            <SkeletonText
              rows={1}
              skeletonHeight={[pxToRem(120), pxToRem(60), pxToRem(85)]}
              width={[pxToRem(200), pxToRem(330), pxToRem(454)]}
              marginBottom={5}
            />
            {!isMobile && (
              <SkeletonText
                rows={1}
                skeletonHeight={[pxToRem(100), pxToRem(40), pxToRem(65)]}
                width={[pxToRem(200), pxToRem(330), pxToRem(554)]}
              />
            )}
          </StyledBlockText>
          <StyledButtonsContainer __css={styles.buttonsContainer(ProductHeroTextColorMode.light)}>
            <SkeletonText
              rows={1}
              skeletonHeight={45}
              width={pxToRem(200)}
            />
            <SkeletonText
              rows={1}
              skeletonHeight={45}
              width={pxToRem(200)}
            />
          </StyledButtonsContainer>
        </StyledContent>
      </StyledContainer>
    </StyledWrapper>
  );
};

export { Hero, HeroSkeleton, HeroProps, getTextColor };
